import type { Campaign } from '@/features/campaigns/types/campaign'
import { objectiveLabel } from '@/features/passes/lib/pass-ui'
import type {
  PassDefinition,
  PassObjectiveDefinition,
  PassObjectiveProgressState
} from '@/features/passes/types/pass'

import { getMissionLockState } from '../domain/getMissionLockState'
import type { Mission, MissionSubmission } from '../types/mission'

// Campaign color tokens cycle by position — there is no per-campaign color
// stored anywhere; the campaign chip's active-state color IS the source of
// truth (design.md Decision 7), this palette is that source.
const CAMPAIGN_COLOR_TOKENS = [
  '#F39D72', // orange
  '#5a9fc9', // light blue
  '#78229f', // purple
  '#4FA37A' // green
] as const

const CATEGORY_COLOR_TOKENS = {
  daily: '#6A81DB',
  weekly: '#6A81DB',
  yearly: '#3457D5',
  general: '#3457D5',
  pass: '#4FA37A',
  claimed: '#9AA3B2' // --brand-gray (globals.css) — muted, nothing left to do
} as const

export type MissionCategorySource =
  | 'daily'
  | 'weekly'
  | 'yearly'
  | 'general'
  | 'pass'
  | 'campaign'
  | 'claimed'

export interface MissionCategory {
  id: string
  source: MissionCategorySource
  label: string
  colorToken: string
  campaignId?: number
}

export interface MissionListItem {
  id: string
  kind: 'mission' | 'pass_objective'
  title: string
  imageUrl?: string
  progress: { current: number; total: number; percentage: number }
  // Mirrors MissionCard.tsx's `!mission.isGeneral` rule — evidence-kind and
  // lifetime missions hide the numeric bar even though _progress has valid
  // (if not very meaningful) numbers. Pass objectives always show one.
  showProgress: boolean
  coinReward?: number
  pointReward?: number
  completed: boolean
  claimed?: boolean
  // Primary category — drives the "Todas" section grouping and the item's color.
  category: MissionCategory
  // Every filter chip this item belongs to. A campaign-linked daily/weekly
  // mission lives in BOTH its campaign chip and its recurrence chip, so tab
  // filtering keys off this list, not the single `category` above.
  categoryIds: string[]
  mission?: Mission
  passObjective?: PassObjectiveDefinition
  submissionStatus?: MissionSubmission['status']
  // Motivo escrito pelo avaliador na recusa. Vem junto do status para o card
  // poder dizer ao aluno o que corrigir, não só que foi recusado.
  submissionFeedback?: string | null
  // Missão com regra de desbloqueio pendente. Decidido pelo backend; o card
  // renderiza estado bloqueado e não abre o modal.
  locked?: boolean
  lockHint?: string
}

export const TODAS_CATEGORY: MissionCategory = {
  id: 'todas',
  source: 'general',
  label: 'Todas',
  colorToken: CATEGORY_COLOR_TOKENS.general
}

export const DAILY_CATEGORY: MissionCategory = {
  id: 'daily',
  source: 'daily',
  label: 'Diárias',
  colorToken: CATEGORY_COLOR_TOKENS.daily
}

export const WEEKLY_CATEGORY: MissionCategory = {
  id: 'weekly',
  source: 'weekly',
  label: 'Semanais',
  colorToken: CATEGORY_COLOR_TOKENS.weekly
}

export const GENERAL_CATEGORY: MissionCategory = {
  id: 'general',
  source: 'general',
  label: 'Missões Anuais',
  colorToken: CATEGORY_COLOR_TOKENS.general
}

export const PASS_CATEGORY: MissionCategory = {
  id: 'pass',
  source: 'pass',
  label: 'Passe Mensal',
  colorToken: CATEGORY_COLOR_TOKENS.pass
}

// Every completed item (claimed missions, completed pass objectives) is
// pulled out of its normal category and grouped here instead, once "Todas"
// is grouping by section — see groupItemsByCategory. Not a filter chip,
// only a section under "Todas" (same treatment as GENERAL_CATEGORY).
export const CLAIMED_CATEGORY: MissionCategory = {
  id: 'claimed',
  source: 'claimed',
  label: 'Resgatadas',
  colorToken: CATEGORY_COLOR_TOKENS.claimed
}

export function campaignCategory(campaign: Campaign, index: number): MissionCategory {
  return {
    id: `campaign-${campaign.id}`,
    source: 'campaign',
    label: campaign.name,
    colorToken: CAMPAIGN_COLOR_TOKENS[index % CAMPAIGN_COLOR_TOKENS.length],
    campaignId: campaign.id
  }
}

/**
 * The recurrence-based category a mission always belongs to, independent of any
 * campaign link — daily/weekly missions, else the general (annual) bucket.
 */
export function recurrenceCategoryForMission(mission: Mission): MissionCategory {
  if (mission.kind === 'progress' && mission.recurrence_kind === 'daily') {
    return DAILY_CATEGORY
  }
  if (mission.kind === 'progress' && mission.recurrence_kind === 'weekly') {
    return WEEKLY_CATEGORY
  }
  return GENERAL_CATEGORY
}

/**
 * The PRIMARY category — used for "Todas" section grouping and the item's color.
 * A campaign-linked mission is grouped under its campaign (so it shows once in
 * "Todas"), otherwise it falls back to its recurrence category. Filter-chip
 * membership is broader — see categoriesForMission.
 */
export function categoryForMission(
  mission: Mission,
  campaignMissionLookup: Map<number, MissionCategory>
): MissionCategory {
  return campaignMissionLookup.get(mission.id) ?? recurrenceCategoryForMission(mission)
}

/**
 * Every category a mission belongs to, campaign first. A campaign-linked
 * daily/weekly mission appears under BOTH its campaign chip and its recurrence
 * chip ("Diárias"/"Semanais") — the campaign link is additive, not exclusive.
 */
export function categoriesForMission(
  mission: Mission,
  campaignMissionLookup: Map<number, MissionCategory>
): MissionCategory[] {
  const recurrence = recurrenceCategoryForMission(mission)
  const campaignMatch = campaignMissionLookup.get(mission.id)
  return campaignMatch ? [campaignMatch, recurrence] : [recurrence]
}

/**
 * One category per campaign, index tied to the campaign's position in the
 * SAME `campaigns` array passed everywhere else — computed once and reused,
 * so a campaign's chip color and its section-header dot color (design.md
 * Decision 7) can never drift from each other via two different index
 * sources (e.g. an original array vs. a filtered one).
 */
export function campaignCategories(campaigns: Campaign[]): MissionCategory[] {
  return campaigns.map((campaign, index) => campaignCategory(campaign, index))
}

/**
 * missionId -> campaign category, built by cross-referencing each campaign's
 * campaign_contents (content_type: 'Mission') against the full missions[]
 * catalog. Missions the client can't see (orphaned references — see
 * design.md Risks) are silently skipped, not rendered as blank cards.
 */
export function buildCampaignMissionLookup(
  campaigns: Campaign[],
  categories: MissionCategory[],
  visibleMissionIds: ReadonlySet<number>
): Map<number, MissionCategory> {
  const lookup = new Map<number, MissionCategory>()

  campaigns.forEach((campaign, index) => {
    const category = categories[index]
    ;(campaign.campaign_contents ?? [])
      .filter(content => content.content_type === 'Mission')
      .forEach(content => {
        if (!visibleMissionIds.has(content.content_id)) return
        lookup.set(content.content_id, category)
      })
  })

  return lookup
}

export function missionToDisplayItem(
  mission: Mission,
  category: MissionCategory,
  categoryIds: string[] = [category.id],
  // A submissão resolvida inteira, e não só o status: o card precisa do motivo
  // da recusa junto, e os dois sempre saem da mesma submissão.
  submission?: MissionSubmission
): MissionListItem {
  const lockState = getMissionLockState(mission)
  const locked = lockState.kind === 'locked'

  return {
    // Missão bloqueada não recebe `id` no payload redigido — o uid sustenta a
    // chave. Sem isso, duas bloqueadas colidiriam em `mission-undefined`.
    id: `mission-${mission.id ?? mission.uid}`,
    kind: 'mission',
    title: mission.name,
    imageUrl: mission.imageUrl,
    progress: {
      current: mission._progress?.current_count ?? 0,
      total: mission._progress?.total_count ?? 1,
      percentage: mission._progress?.percentage ?? 0
    },
    // Missão bloqueada não expõe recompensa nem progresso — não há o que
    // mostrar, e mostrar zerado sugeriria que o aluno já começou.
    showProgress: !locked && !mission.isGeneral,
    coinReward: locked ? undefined : mission.coin_reward,
    pointReward: locked ? undefined : mission.point_reward,
    completed: mission._progress?.completed ?? false,
    claimed: mission._progress?.claimed ?? false,
    category,
    categoryIds,
    mission,
    submissionStatus: locked ? undefined : submission?.status,
    submissionFeedback: locked ? undefined : submission?.review_feedback,
    locked,
    lockHint: locked ? lockState.hint : undefined
  }
}

// Reward earned but not collected — MissionModal#handleClaimReward is a
// manual tap, not automatic. Pass objectives have no per-objective claim
// step (tier rewards are delivered separately), so they're never claimable.
export function isClaimable(item: MissionListItem): boolean {
  return item.kind === 'mission' && item.completed && item.claimed === false
}

export function passObjectiveToDisplayItem(
  definition: PassObjectiveDefinition,
  progress: PassObjectiveProgressState | undefined
): MissionListItem {
  const current = progress?.current_count ?? 0
  return {
    id: `pass-objective-${definition.id}`,
    kind: 'pass_objective',
    title: objectiveLabel(definition),
    progress: {
      current,
      total: definition.quantity,
      percentage: definition.quantity
        ? Math.round((current / definition.quantity) * 100)
        : 0
    },
    showProgress: true,
    coinReward: definition.coin_reward,
    completed: progress?.completed ?? false,
    category: PASS_CATEGORY,
    categoryIds: [PASS_CATEGORY.id],
    passObjective: definition
  }
}

// Display order for "Todas" section headers — not specified by product,
// chosen to match the reference screenshot (Passe, then Campanha, then the
// less time-boxed categories). 'claimed' is always last: everything in it
// is already done, lowest priority regardless of what it originally was.
const SECTION_ORDER: MissionCategorySource[] = [
  'pass',
  'campaign',
  'general',
  'daily',
  'weekly',
  'claimed'
]

export interface MissionSection {
  category: MissionCategory
  items: MissionListItem[]
}

export function groupItemsByCategory(items: MissionListItem[]): MissionSection[] {
  const sections = new Map<string, MissionSection>()

  items.forEach(item => {
    // Completed items (claimed missions, completed pass objectives) leave
    // their normal category and join one shared "Resgatadas" section at
    // the end, instead of sitting at the bottom of e.g. "Diárias".
    const category = item.completed ? CLAIMED_CATEGORY : item.category
    const existing = sections.get(category.id)
    if (existing) {
      existing.items.push(item)
    } else {
      sections.set(category.id, { category, items: [item] })
    }
  })

  return [...sections.values()].sort((a, b) => {
    const rankA = SECTION_ORDER.indexOf(a.category.source)
    const rankB = SECTION_ORDER.indexOf(b.category.source)
    if (rankA !== rankB) return rankA - rankB
    return a.category.label.localeCompare(b.category.label, 'pt-BR')
  })
}

export function buildPassDisplayItems(
  passDefinition: PassDefinition | null | undefined,
  passObjectiveProgresses: PassObjectiveProgressState[]
): MissionListItem[] {
  if (!passDefinition) return []

  const progressByObjectiveId = new Map(
    passObjectiveProgresses.map(state => [state.pass_objective_id, state])
  )

  return passDefinition.pass_objectives.map(objective =>
    passObjectiveToDisplayItem(objective, progressByObjectiveId.get(objective.id))
  )
}
